import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText, Library, Plus, Search, Database, Filter } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  cn,
  useToast,
  type SelectOption,
} from '../../ui';
import type { KbSource } from '../../api';
import { useDocuments } from '../../features/documents/useDocuments';
import { CitationsPanel } from '../Chat/CitationsPanel';
import {
  collectFacet,
  collectScalar,
  collectYears,
  filterDocuments,
  searchDocuments,
  sortDocuments,
} from '../../features/documents/documentModel';
import { DOCUMENT_TYPES } from '../../features/documents/documentConfig';
import type {
  DocumentFilter,
  TeamDocument,
  TeamDocumentSummary,
} from '../../features/documents/types';
import { DocumentCard } from './DocumentCard';
import { DocumentModal } from './DocumentModal';

const EMPTY_FILTER: DocumentFilter = {
  docType: '',
  status: '',
  service: '',
  author: '',
  owner: '',
  reviewer: '',
  goalId: '',
  tag: '',
  year: '',
  query: '',
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'IN_REVIEW', label: 'In review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DEPRECATED', label: 'Deprecated' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const DOC_TYPE_OPTIONS: SelectOption[] = DOCUMENT_TYPES.map((t) => ({
  value: t.value,
  label: t.label,
}));

/**
 * Document Bank — a discoverable metadata index over docs living in Quip / Wiki
 * / etc. Register a doc (link + metadata, optionally AI-structured), then filter
 * by type / status / service / author / owner / reviewer / goal / tag / year, or
 * run a client-side semantic-ish search over title / summary / tags / context.
 */
export function DocumentsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    documents,
    goals,
    workstreams,
    tasks,
    services,
    decisions,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    serviceTitles,
    decisionTitles,
    loading,
    error,
    create,
    update,
    remove,
    structureFromNotes,
    kbSearch,
  } = useDocuments();

  const [filter, setFilter] = useState<DocumentFilter>(EMPTY_FILTER);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<TeamDocument | undefined>(undefined);
  const [pendingDelete, setPendingDelete] =
    useState<TeamDocumentSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Discovery mode: client-side filtering vs semantic (KB) retrieval.
  const [mode, setMode] = useState<'filter' | 'semantic'>('filter');
  const [semanticQuery, setSemanticQuery] = useState('');
  const [kbResults, setKbResults] = useState<KbSource[] | null>(null);
  const [kbSearching, setKbSearching] = useState(false);
  const [kbError, setKbError] = useState<string | null>(null);

  const runSemantic = async () => {
    const q = semanticQuery.trim();
    if (!q || kbSearching) return;
    setKbSearching(true);
    setKbError(null);
    try {
      const sources = await kbSearch(q, 10);
      setKbResults(sources);
    } catch (e) {
      setKbError(
        e instanceof Error
          ? e.message
          : 'Knowledge base search failed (KB access / model may not be enabled).',
      );
      setKbResults(null);
    } finally {
      setKbSearching(false);
    }
  };

  const authorOptions = useMemo(
    () => collectFacet(documents, (d) => d.authors).map((v) => ({ value: v, label: v })),
    [documents],
  );
  const ownerOptions = useMemo(
    () => collectScalar(documents, (d) => d.ownerPoc).map((v) => ({ value: v, label: v })),
    [documents],
  );
  const reviewerOptions = useMemo(
    () => collectFacet(documents, (d) => d.reviewers).map((v) => ({ value: v, label: v })),
    [documents],
  );
  const serviceFacetOptions = useMemo(
    () =>
      collectFacet(documents, (d) => d.relatedServices).map((v) => ({
        value: v,
        label: serviceTitles.get(v) ?? v,
      })),
    [documents, serviceTitles],
  );
  const tagOptions = useMemo(
    () => collectFacet(documents, (d) => d.tags).map((v) => ({ value: v, label: `#${v}` })),
    [documents],
  );
  const yearOptions = useMemo(
    () => collectYears(documents).map((v) => ({ value: v, label: v })),
    [documents],
  );
  const goalOptions = useMemo(
    () => goals.map((g) => ({ value: g.id, label: g.title || g.id })),
    [goals],
  );

  const filtered = useMemo(() => {
    const byFilter = filterDocuments(documents, filter);
    return filter.query.trim()
      ? searchDocuments(byFilter, filter.query)
      : sortDocuments(byFilter);
  }, [documents, filter]);

  const filtersActive =
    !!filter.docType ||
    !!filter.status ||
    !!filter.service ||
    !!filter.author ||
    !!filter.owner ||
    !!filter.reviewer ||
    !!filter.goalId ||
    !!filter.tag ||
    !!filter.year ||
    !!filter.query;

  const setField = <K extends keyof DocumentFilter>(key: K, value: DocumentFilter[K]) =>
    setFilter((f) => ({ ...f, [key]: value }));

  const openRegister = () => {
    setEditingDoc(undefined);
    setModalOpen(true);
  };

  const onSubmit = async (draft: Parameters<typeof create>[0]) => {
    if (editingDoc) {
      await update(editingDoc.id, draft);
      toast({ tone: 'success', title: 'Document updated', description: draft.title });
    } else {
      await create(draft);
      toast({ tone: 'success', title: 'Document registered', description: draft.title });
    }
  };

  const onDelete = (d: TeamDocumentSummary) => setPendingDelete(d);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Document removed', description: pendingDelete.title });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove document',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={<Library className="h-5 w-5" />}
        title="Document Bank"
        description="A discoverable index of design docs, RFCs, runbooks and more — links + context, not content."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openRegister}>
            Register document
          </Button>
        }
      />

      {/* Discovery mode toggle. */}
      <div className="mb-4 inline-flex overflow-hidden rounded-lg border border-surface-border">
        <button
          type="button"
          onClick={() => setMode('filter')}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
            mode === 'filter'
              ? 'bg-accent-soft/60 text-zinc-100'
              : 'bg-surface-1 text-zinc-400 hover:text-zinc-200',
          )}
        >
          <Filter className="h-3.5 w-3.5" /> Filters
        </button>
        <button
          type="button"
          onClick={() => setMode('semantic')}
          className={cn(
            'inline-flex items-center gap-1.5 border-l border-surface-border px-3 py-1.5 text-xs font-medium transition-colors',
            mode === 'semantic'
              ? 'bg-accent-soft/60 text-zinc-100'
              : 'bg-surface-1 text-zinc-400 hover:text-zinc-200',
          )}
        >
          <Database className="h-3.5 w-3.5" /> Semantic (KB)
        </button>
      </div>

      {mode === 'semantic' && (
        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Semantic knowledge-base search"
                placeholder="Describe what you're looking for…"
                value={semanticQuery}
                onChange={(e) => setSemanticQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void runSemantic();
                }}
              />
            </div>
            <Button
              onClick={() => void runSemantic()}
              loading={kbSearching}
              disabled={!semanticQuery.trim()}
              leftIcon={!kbSearching ? <Search className="h-4 w-4" /> : undefined}
            >
              Search
            </Button>
          </div>
          {kbSearching ? (
            <div className="flex items-center justify-center py-16">
              <Spinner label="Searching knowledge base" />
            </div>
          ) : kbError ? (
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" />}
              title="Knowledge base search failed"
              description={kbError}
            />
          ) : kbResults === null ? (
            <EmptyState
              icon={<Database className="h-6 w-6" />}
              title="Search the knowledge base"
              description="Semantic search retrieves the most relevant passages from the team knowledge base (Bedrock Retrieve), complementing the client-side filters."
            />
          ) : kbResults.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-6 w-6" />}
              title="No passages found"
              description="Try rephrasing your query."
            />
          ) : (
            <CitationsPanel citations={[{ text: '', sources: kbResults }]} />
          )}
        </div>
      )}

      {mode === 'filter' && (
        <>
      {/* Search. */}
      <div className="mb-4">
        <Input
          placeholder="Search title, summary, tags, context…"
          value={filter.query}
          onChange={(e) => setField('query', e.target.value)}
          className="pl-9"
        />
        <Search className="pointer-events-none -mt-[26px] ml-3 h-4 w-4 text-zinc-500" aria-hidden="true" />
      </div>

      {/* Discovery filters. */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Select label="Type" placeholder="All types" value={filter.docType} options={DOC_TYPE_OPTIONS} onChange={(e) => setField('docType', e.target.value)} />
        <Select label="Status" placeholder="All statuses" value={filter.status} options={STATUS_OPTIONS} onChange={(e) => setField('status', e.target.value)} />
        <Select label="Service" placeholder="All services" value={filter.service} options={serviceFacetOptions} onChange={(e) => setField('service', e.target.value)} />
        <Select label="Author" placeholder="All authors" value={filter.author} options={authorOptions} onChange={(e) => setField('author', e.target.value)} />
        <Select label="Owner" placeholder="All owners" value={filter.owner} options={ownerOptions} onChange={(e) => setField('owner', e.target.value)} />
        <Select label="Reviewer" placeholder="All reviewers" value={filter.reviewer} options={reviewerOptions} onChange={(e) => setField('reviewer', e.target.value)} />
        <Select label="Goal" placeholder="All goals" value={filter.goalId} options={goalOptions} onChange={(e) => setField('goalId', e.target.value)} />
        <Select label="Tag" placeholder="All tags" value={filter.tag} options={tagOptions} onChange={(e) => setField('tag', e.target.value)} />
        <Select label="Year" placeholder="All years" value={filter.year} options={yearOptions} onChange={(e) => setField('year', e.target.value)} />
      </div>

      <div className="mb-4 flex items-center gap-3">
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs text-zinc-500">
          {filtered.length} of {documents.length} {documents.length === 1 ? 'document' : 'documents'}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading documents" />
        </div>
      ) : error ? (
        <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Couldn't load documents" description={error} />
      ) : documents.length === 0 ? (
        <EmptyState
          icon={<Library className="h-6 w-6" />}
          title="No documents registered"
          description="Register your first design doc, RFC or runbook so the team can find it — and connect it to services, goals and decisions."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openRegister}>
              Register document
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-6 w-6" />}
          title="No documents match"
          description="Try clearing filters or broadening your search."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {filtered.map((d) => (
            <DocumentCard
              key={d.id}
              doc={d}
              goalTitle={d.relatedGoalId ? goalTitles.get(d.relatedGoalId) : undefined}
              workstreamTitle={d.relatedWorkstreamId ? workstreamTitles.get(d.relatedWorkstreamId) : undefined}
              serviceTitles={serviceTitles}
              onOpen={(id) => navigate(`/documents/${encodeURIComponent(id)}`)}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
        </>
      )}

      <DocumentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultOwner={currentAlias}
        initialDoc={editingDoc}
        goals={goals}
        workstreams={workstreams}
        tasks={tasks}
        services={services}
        decisions={decisions}
        documents={documents}
        serviceTitles={serviceTitles}
        storyTitles={storyTitles}
        decisionTitles={decisionTitles}
        onSubmit={onSubmit}
        structureFromNotes={structureFromNotes}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove document?"
        confirmLabel="Remove document"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
        message={
          <>
            Remove{' '}
            <span className="font-medium text-zinc-100">{pendingDelete?.title}</span>{' '}
            from the document bank? This removes the index entry (not the linked
            doc) and can't be undone.
          </>
        }
      />
    </div>
  );
}

export default DocumentsPage;
